import apiClient from './apiClient';

/**
 * 独立服务器 (dedicated servers resold from ReliableSite) — user: /api/user/dedicated/*, admin: /api/admin/dedicated/*.
 * Every call resolves to { status, msg, data }; network/HTTP errors become { status: 'error', msg }.
 */
const wrap = async (p) => {
    try {
        const r = await p;
        return r.data;
    } catch (e) {
        const d = e.response?.data || {};
        return { status: 'error', msg: d.msg || d.error || d.message || e.message };
    }
};

export const isOk = (r) => r && (r.status === 'success' || r.code === 0 || r.success === true);
export const errMsg = (r, fallback) => (r && (r.msg || r.error || r.message)) || fallback;
export const arr = (v) => (Array.isArray(v) ? v : []);

export const dedicatedUserApi = {
    catalog: () => wrap(apiClient.get('/api/user/dedicated/catalog')),
    model: (id) => wrap(apiClient.get(`/api/user/dedicated/catalog/${id}`)),
    quote: (data) => wrap(apiClient.post('/api/user/dedicated/quote', data)),
    order: (data) => wrap(apiClient.post('/api/user/dedicated/order', data)),
    list: () => wrap(apiClient.get('/api/user/dedicated/servers')),
    get: (id) => wrap(apiClient.get(`/api/user/dedicated/servers/${id}`)),
    password: (id) => wrap(apiClient.get(`/api/user/dedicated/servers/${id}/password`)),
    power: (id, action) => wrap(apiClient.post(`/api/user/dedicated/servers/${id}/power`, { action })),
    kvm: (id) => wrap(apiClient.post(`/api/user/dedicated/servers/${id}/kvm`)),
    osList: (id) => wrap(apiClient.get(`/api/user/dedicated/servers/${id}/os`)),
    partitioning: (id, osId) => wrap(apiClient.get(`/api/user/dedicated/servers/${id}/partitioning`, { params: { os_id: osId } })),
    reinstall: (id, data) => wrap(apiClient.post(`/api/user/dedicated/servers/${id}/reinstall`, data)),
    reinstallStatus: (id) => wrap(apiClient.get(`/api/user/dedicated/servers/${id}/reinstall`)),
    bandwidth: (id, period) => wrap(apiClient.get(`/api/user/dedicated/servers/${id}/bandwidth`, { params: { period } })),
    rdnsGet: (id, params) => wrap(apiClient.get(`/api/user/dedicated/servers/${id}/rdns`, { params })),
    rdnsSet: (id, data) => wrap(apiClient.post(`/api/user/dedicated/servers/${id}/rdns`, data)),
    reinstallCancel: (id) => wrap(apiClient.post(`/api/user/dedicated/servers/${id}/reinstall/cancel`)),
    rescueStatus: (id) => wrap(apiClient.get(`/api/user/dedicated/servers/${id}/rescue`)),
    rescue: (id, data) => wrap(apiClient.post(`/api/user/dedicated/servers/${id}/rescue`, data)),
    nullRoutes: (id) => wrap(apiClient.get(`/api/user/dedicated/servers/${id}/null-routes`)),
    nullRouteAdd: (id, data) => wrap(apiClient.post(`/api/user/dedicated/servers/${id}/null-routes`, data)),
    nullRouteRemove: (id, data) => wrap(apiClient.post(`/api/user/dedicated/servers/${id}/null-routes/remove`, data)),
    ddos: (id) => wrap(apiClient.get(`/api/user/dedicated/servers/${id}/ddos`)),
    backup: (id) => wrap(apiClient.get(`/api/user/dedicated/servers/${id}/backup`)),
    backupAccount: (id, data) => wrap(apiClient.post(`/api/user/dedicated/servers/${id}/backup/account`, data)),
};

/** The management tools (ServerTools) talk to one of these: the customer's own API, or the admin op endpoint. */
export const userTools = (id) => ({
    power: (action) => dedicatedUserApi.power(id, action),
    kvm: () => dedicatedUserApi.kvm(id),
    osList: () => dedicatedUserApi.osList(id),
    partitioning: (osId) => dedicatedUserApi.partitioning(id, osId),
    reinstall: (data) => dedicatedUserApi.reinstall(id, data),
    reinstallStatus: () => dedicatedUserApi.reinstallStatus(id),
    reinstallCancel: () => dedicatedUserApi.reinstallCancel(id),
    rescueStatus: () => dedicatedUserApi.rescueStatus(id),
    rescue: (data) => dedicatedUserApi.rescue(id, data),
    bandwidth: (period) => dedicatedUserApi.bandwidth(id, period),
    rdnsGet: (params) => dedicatedUserApi.rdnsGet(id, params),
    rdnsSet: (data) => dedicatedUserApi.rdnsSet(id, data),
    nullRoutes: () => dedicatedUserApi.nullRoutes(id),
    nullRouteAdd: (data) => dedicatedUserApi.nullRouteAdd(id, data),
    nullRouteRemove: (data) => dedicatedUserApi.nullRouteRemove(id, data),
    ddos: () => dedicatedUserApi.ddos(id),
    backup: () => dedicatedUserApi.backup(id),
    backupAccount: (data) => dedicatedUserApi.backupAccount(id, data),
});

export const adminTools = (id) => {
    const op = (name, data = {}) => wrap(apiClient.post(`/api/admin/dedicated/servers/${id}/op`, { op: name, ...data }));
    return {
        power: (action) => dedicatedAdminApi.power(id, action),
        kvm: () => op('kvm'),
        osList: () => op('os_list'),
        partitioning: (osId) => op('partitioning', { os_id: osId }),
        reinstall: (data) => op('reinstall', data),
        reinstallStatus: () => op('reinstall_status'),
        reinstallCancel: () => op('reinstall_cancel'),
        rescueStatus: () => op('rescue_status'),
        rescue: (data) => op('rescue', data),
        bandwidth: (period) => op('bandwidth', { period }),
        rdnsGet: (params) => op('rdns_get', params),
        rdnsSet: (data) => op('rdns_set', data),
        nullRoutes: () => op('null_routes'),
        nullRouteAdd: (data) => op('null_route_add', data),
        nullRouteRemove: (data) => op('null_route_remove', data),
        ddos: () => op('ddos'),
        backup: () => op('backup'),
        backupAccount: (data) => op('backup_account', data),
    };
};

export const dedicatedAdminApi = {
    list: (params) => wrap(apiClient.get('/api/admin/dedicated/servers', { params })),
    get: (id) => wrap(apiClient.get(`/api/admin/dedicated/servers/${id}`)),
    password: (id) => wrap(apiClient.get(`/api/admin/dedicated/servers/${id}/password`)),
    retry: (id) => wrap(apiClient.post(`/api/admin/dedicated/servers/${id}/retry`)),
    link: (id, serverId) => wrap(apiClient.post(`/api/admin/dedicated/servers/${id}/link`, { server_id: serverId })),
    refresh: (id) => wrap(apiClient.post(`/api/admin/dedicated/servers/${id}/refresh`)),
    power: (id, action) => wrap(apiClient.post(`/api/admin/dedicated/servers/${id}/power`, { action })),
    markCancelled: (id) => wrap(apiClient.post(`/api/admin/dedicated/servers/${id}/cancelled`)),
    cancelRefund: (id) => wrap(apiClient.post(`/api/admin/dedicated/servers/${id}/cancel-refund`)),
    unlinked: () => wrap(apiClient.get('/api/admin/dedicated/servers/unlinked')),
    catalog: (fresh) => wrap(apiClient.get('/api/admin/dedicated/catalog', { params: fresh ? { fresh: 1 } : {} })),
    settings: (data) => wrap(apiClient.post('/api/admin/dedicated/settings', data)),
    model: (id) => wrap(apiClient.get(`/api/admin/dedicated/catalog/${id}`)),
    quote: (data) => wrap(apiClient.post('/api/admin/dedicated/quote', data)),
    orderFor: (data) => wrap(apiClient.post('/api/admin/dedicated/order', data)),
};

/** Status → [color, English label key] for the customer and admin views. */
export const STATUS = {
    awaiting_payment: ['default', 'Awaiting payment'],
    ordering: ['processing', 'Provisioning'],
    provisioning: ['processing', 'Provisioning'],
    active: ['green', 'Running'],
    suspended: ['orange', 'Suspended'],
    cancel_requested: ['default', 'Cancelling'],
    cancelled: ['default', 'Cancelled'],
    failed: ['red', 'Provisioning failed'],
};

export const DC_LABEL = { LAX: 'Los Angeles', MIA: 'Miami', PNJ: 'New Jersey', NL: 'Amsterdam' };
export const CYCLES = ['monthly', 'quarterly', 'semi_annually', 'annually', 'biennially', 'triennially'];
export const CYCLE_LABEL = {
    monthly: 'Monthly', quarterly: 'Quarterly', semi_annually: 'Semi-annually',
    annually: 'Annually', biennially: 'Every 2 years', triennially: 'Every 3 years',
};
